import { useState, useEffect } from 'react';
import { getSystemSettings } from '../services/systemSettingsService';
import { currencies } from '../utils/constants';

interface CurrencyInfo {
  code: string;
  symbol: string;
  label: string;
}

/**
 * Hook لاستخدام العملة الافتراضية من إعدادات النظام
 */
export const useCurrency = () => {
  const [currency, setCurrency] = useState<CurrencyInfo>({
    code: 'EGP',
    symbol: 'ج.م',
    label: 'جنيه مصري'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCurrency = async () => {
      try {
        const settings = await getSystemSettings();
        const currencyCode = settings.currency || 'EGP';
        
        // البحث عن معلومات العملة
        const currencyInfo = currencies.find(c => c.value === currencyCode);
        
        if (currencyInfo) {
          setCurrency({
            code: currencyInfo.value,
            symbol: currencyInfo.symbol,
            label: currencyInfo.label
          });
        }
      } catch (error) {
        console.error('Error loading currency settings:', error);
        // استخدام العملة الافتراضية في حالة الخطأ
      } finally {
        setLoading(false);
      }
    };

    loadCurrency();
  }, []);

  /**
   * تنسيق المبلغ بالعملة الحالية
   */
  const formatCurrency = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return `0.00 ${currency.symbol}`;
    }
    return `${amount.toFixed(2)} ${currency.symbol}`;
  };

  /**
   * تنسيق المبلغ بالعملة الحالية مع فواصل الآلاف
   */
  const formatCurrencyWithCommas = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return `0.00 ${currency.symbol}`;
    }
    return `${amount.toLocaleString('ar-EG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })} ${currency.symbol}`;
  };

  /**
   * تنسيق المبلغ بدون رمز العملة
   */
  const formatAmount = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return '0.00';
    }
    return amount.toFixed(2);
  };

  /**
   * تنسيق المبلغ بدون رمز العملة مع فواصل الآلاف
   */
  const formatAmountWithCommas = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return '0.00';
    }
    return amount.toLocaleString('ar-EG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  /**
   * الحصول على رمز العملة فقط
   */
  const getCurrencySymbol = (): string => {
    return currency.symbol;
  };

  /**
   * الحصول على كود العملة فقط
   */
  const getCurrencyCode = (): string => {
    return currency.code;
  };

  /**
   * الحصول على اسم العملة
   */
  const getCurrencyLabel = (): string => {
    return currency.label;
  };

  return {
    currency,
    loading,
    formatCurrency,
    formatCurrencyWithCommas,
    formatAmount,
    formatAmountWithCommas,
    getCurrencySymbol,
    getCurrencyCode,
    getCurrencyLabel
  };
};
